import React from 'react';
import PropTypes from 'prop-types';
import Integration from './integration';
import Manage from './manage';
import { Button } from 'antd';
import styles from './index.module.css';
import * as translations from './strings';
import { getLangKey } from 'appJS/models/Application';
import {
  integrationEntriesForKind,
  integrationInfoForCode
} from '../../integration_settings/integrationsInfo';
import integrationsBanner from '../../integration_settings/images/integrations-banner.png';
import { INTEGRATION_CATEGORY_TABS } from '../../integration_settings/categoryTabs';
import categoryTabStyles from '../../integration_settings/categoryTabs.module.css';
import IntegrationMessagesPanel from './manage/IntegrationMessagesPanel';

const strings = translations[getLangKey()];

const organizationManagedConnector = code => {
  const integrationInfo = integrationInfoForCode(code);

  return code !== 'zapier' && integrationInfo.kind !== 'mcp';
};

export default class Integrations extends React.Component {
  static propTypes = {
    org: PropTypes.object,
    setParentState: PropTypes.func,
    isLoading: PropTypes.bool,
    tempZapierData: PropTypes.object,
    user: PropTypes.object,
    accessTo: PropTypes.shape({
      billing: PropTypes.bool
    }),
    navigate: PropTypes.func
  };

  state = { activeCategory: 'all' };

  recordValue = (record, key, fallbackIndex) => {
    if (!record) return null;

    if (Array.isArray(record)) {
      const pair = record.find(
        item => Array.isArray(item) && String(item[0]) === key
      );

      if (pair) return pair[1];
      return record[fallbackIndex];
    }

    return record[key];
  };

  connectorCode = connector => this.recordValue(connector, 'code', 0);

  connectorName = connector => this.recordValue(connector, 'name', 1);

  integrationConnectorCode = integration =>
    this.connectorCode(integration?.connector || integration?.['connector']);

  integrationConnectorName = integration =>
    this.connectorName(integration?.connector || integration?.['connector']);

  includeConnectorCode = code => {
    if (code !== 'slack_legacy') return true;

    const { org } = this.props;
    const hasSlackConnector = (org.connectors || []).some(
      connector => this.connectorCode(connector) === 'slack'
    );
    if (!hasSlackConnector) return true;

    return (org.integrations || []).some(integration => {
      return this.integrationConnectorCode(integration) === 'slack_legacy';
    });
  };

  integrationCodes = () => {
    const { org } = this.props;
    const codes = [];
    const addCode = code => {
      if (typeof code !== 'string') return;
      if (!organizationManagedConnector(code)) return;
      if (!this.includeConnectorCode(code)) return;
      if (codes.includes(code)) return;

      codes.push(code);
    };

    (org.connectors || []).forEach(connector => {
      addCode(this.connectorCode(connector));
    });

    return codes;
  };

  integrationCodesForDisplay = () => [
    ...this.integrationCodes(),
    ...integrationEntriesForKind('mcp').map(([code]) => code)
  ];

  getIntegration = value =>
    (this.props.org.integrations || []).find(
      integration => this.integrationConnectorCode(integration) === value
    );

  manageIntegration = integrations => {
    const { org } = this.props;
    const integrationPath = window.location.pathname.match(
      /\/integrations\/(\d+)\/(edit|activity)/
    );
    const integrationId = integrationPath?.[1];
    const integrationPage = integrationPath?.[2];
    const connectorCode = window.location.pathname.match(
      /\/integrations\/new\/([^/]+)/
    )?.[1];
    if (!integrationId && !connectorCode) return null;

    const integration = integrationId
      ? integrations.find(int => String(int.integrationId) === integrationId)
      : integrations.find(
          int => int.code === decodeURIComponent(connectorCode)
        );
    if (!integration) return null;

    const integrationInfo = integrationInfoForCode(integration.code);
    if (integrationInfo.kind === 'mcp') return null;

    if (integrationPage === 'activity') {
      return (
        <IntegrationMessagesPanel
          organizationId={org.organization.id}
          integrationId={integration.integrationId}
          integrationName={integration.title}
        />
      );
    }

    return (
      <Manage
        connected={integration.isConnected}
        apiKey={org.organization.api_key}
        orgName={org.organization.name}
        orgId={org.organization.id}
        integrationId={integration.integrationId}
        code={integration.code}
        title={integration.title}
        about={integration.about}
        articleId={integration.articleId}
        icon={integrationInfo.icon}
        projectConnections={integration.intInfo?.projects || []}
        organizationProjects={org.projects || []}
        authProviders={org.authProviders || []}
        jiraAuthentication={integration.intInfo?.jiraAuthentication}
      />
    );
  };

  getIntegrationContent = code => {
    const { org, tempZapierData } = this.props;
    const integration = this.getIntegration(code);
    const connector = (org.connectors || []).find(
      connector => this.connectorCode(connector) === code
    );
    const integrationInfo = integrationInfoForCode(code);
    if (!connector && !integration && code !== 'zapier') return;
    const connectorName =
      this.connectorName(connector) ||
      this.integrationConnectorName(integration) ||
      integrationInfo.label ||
      code;
    let isConnected = integration ? integration.isConnected : false;
    const getId = () => {
      if (integration) {
        return integration.id;
      } else if (tempZapierData) {
        return tempZapierData.id;
      }
      return null;
    };
    const data = {
      intInfo: integration,
      title: integrationInfo.label || connectorName,
      code: code,
      icon: integrationInfo.icon,
      articleId: integrationInfo.articleId,
      isConnected,
      primaryButton: isConnected ? 'Manage' : 'Connect',
      orgId: org.organization.id,
      integrationId: getId(),
      category: integrationInfo.category || 'other',
      img: {
        className: code,
        alt: `${connectorName} logo`
      },
      key: code
    };

    switch (code) {
      case 'zapier':
        data.about = strings.zapierAbout;
        break;
      case 'slack_legacy':
        data.about = strings.slackAbout;
        break;
      case 'github':
        data.about = strings.githubAbout;
        break;
      case 'bcx':
        data.about = strings.bcxAbout;
        break;
      case 'harvest':
        data.about = strings.harvestAbout;
        break;
    }
    data.about = data.about || integrationInfo.description;

    return data;
  };

  buildIntegration = code => {
    const { org } = this.props;
    const integration = (org.integrations || []).find(
      item => this.integrationConnectorCode(item) === code
    );
    const connector = (org.connectors || []).find(
      item => this.connectorCode(item) === code
    );
    const integrationInfo = integrationInfoForCode(code);
    const connectorName =
      this.connectorName(connector) ||
      this.integrationConnectorName(integration) ||
      code;
    const isConnected = !!integration;
    const isMcpIntegration = integrationInfo.kind === 'mcp';
    let primaryButton = isConnected ? 'Manage' : 'Connect';
    if (isMcpIntegration) primaryButton = 'Install Guide';

    return {
      intInfo: integration,
      title: integrationInfo.label || connectorName,
      code,
      icon: integrationInfo.icon,
      articleId: integrationInfo.articleId,
      category: integrationInfo.category || 'other',
      isConnected,
      primaryButton,
      orgId: org.organization.id,
      integrationId: integration?.id,
      img: {
        className: code,
        alt: `${connectorName} logo`
      },
      key: code,
      about:
        code === 'slack_legacy'
          ? strings.slackAbout
          : code === 'github'
          ? strings.githubAbout
          : code === 'bcx'
          ? strings.bcxAbout
          : code === 'harvest'
          ? strings.harvestAbout
          : integrationInfo.description
    };
  };

  filteredIntegrations = integrations => {
    const { activeCategory } = this.state;
    if (activeCategory === 'all') return integrations;

    return integrations.filter(
      integration => integration.category === activeCategory
    );
  };

  render() {
    const { org, accessTo } = this.props;
    const data = this.getIntegrationContent;
    const zapier = data('zapier');
    const connectorCodes = (org.connectors || []).map(connector =>
      this.connectorCode(connector)
    );
    const integrationCodes = (org.integrations || []).map(integration =>
      this.integrationConnectorCode(integration)
    );
    const integrations = this.integrationCodesForDisplay()
      .map(code => this.buildIntegration(code))
      .filter(Boolean);
    if (zapier && org.zapierAllowed) integrations.push(zapier);
    integrations.sort((a, b) => a.title.localeCompare(b.title));
    const filteredIntegrations = this.filteredIntegrations(integrations);
    const manageIntegration = this.manageIntegration(integrations);

    if (manageIntegration) {
      return (
        <div className={styles.componentContainer}>{manageIntegration}</div>
      );
    }

    return (
      <div
        className={styles.componentContainer}
        data-connectors-count={(org.connectors || []).length}
        data-integrations-count={(org.integrations || []).length}
        data-derived-integrations-count={integrations.length}
        data-derivation-version="connected-inline-v4"
        data-connector-codes={connectorCodes.join(',')}
        data-integration-codes={integrationCodes.join(',')}
      >
        <div className={styles.integrationContainer}>
          <div className={styles.integrationsBanner}>
            <div className={styles.integrationsHeader}>
              <h1>{strings.integrations}</h1>
              <p className={styles.integrationsDescription}>
                {strings.integrationsDescription}
              </p>
            </div>
            <img
              className={styles.bannerImage}
              src={integrationsBanner}
              alt="integrations_banner"
            />
          </div>

          <div className={categoryTabStyles.categoryTabs}>
            {INTEGRATION_CATEGORY_TABS.map(tab => (
              <Button
                key={tab.value}
                shape="round"
                type={
                  this.state.activeCategory === tab.value
                    ? 'primary'
                    : 'default'
                }
                className={
                  this.state.activeCategory === tab.value
                    ? categoryTabStyles.categoryTabActive
                    : categoryTabStyles.categoryTab
                }
                onClick={() => this.setState({ activeCategory: tab.value })}
              >
                {tab.label}
              </Button>
            ))}
          </div>

          {filteredIntegrations.length ? (
            <div className={styles.integrationGrid}>
              {filteredIntegrations.map(integration => (
                <Integration
                  key={integration.key}
                  {...integration}
                  integrationAllowed
                  billingRights={accessTo.billing}
                  subscriptionLink={org.subscriptionLink}
                  navigate={this.props.navigate}
                />
              ))}
            </div>
          ) : (
            <p className={styles.emptyState}>
              No organization integrations are available.
            </p>
          )}

          <div className={styles.suggestionAlert}>
            <span>What are we missing?</span>
            <a href="https://bugherd.typeform.com/to/e9bhE6vO" target="_blank">
              Suggest an integration
            </a>
          </div>
        </div>
      </div>
    );
  }
}
